'use client';

import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, FormEvent } from "react";
import {
  Menu,
  X,
  Calculator,
  Search,
  SunMedium,
  MoonStar,
} from "lucide-react";

export default function Header() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const isActive = (path: string) => pathname === path;

  const isToolsCategoriesActive = pathname === "/tools" && searchParams.get('view') === 'categories';

  useEffect(() => {
    if (typeof window === "undefined") return;

    const stored = window.localStorage.getItem("toolvix-theme");
    // Default to light theme instead of system preference
    const nextIsDark = stored === "dark";

    setIsDark(nextIsDark);
    document.documentElement.classList.toggle("dark", nextIsDark);
  }, []);

  const toggleTheme = () => {
    const nextIsDark = !isDark;
    setIsDark(nextIsDark);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("toolvix-theme", nextIsDark ? "dark" : "light");
    }
    document.documentElement.classList.toggle("dark", nextIsDark);
  };

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    router.push(`/tools?search=${encodeURIComponent(searchQuery.trim())}`);
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl dark:bg-[#020617]/80">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <div className="flex flex-shrink-0 items-center">
          <Link href="/" className="group flex items-center space-x-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-br from-[#4F46E5] via-[#6366F1] to-[#7C3AED] shadow-lg shadow-indigo-500/30 transition-transform group-hover:scale-110">
              <Calculator className="h-6 w-6 text-white" />
            </div>
            <div className="flex flex-col">
              <span className="bg-gradient-to-r from-[#111827] via-[#1F2937] to-[#4B5563] bg-clip-text text-xl font-semibold tracking-tight text-transparent dark:from-slate-50 dark:via-slate-100 dark:to-slate-300">
                Toolvix
              </span>
              <span className="text-xs font-medium text-gray-500 dark:text-slate-400">
                140+ free online tools
              </span>
            </div>
          </Link>
        </div>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 text-sm font-medium text-gray-600 dark:text-slate-300 lg:flex">
          <Link
            href="/tools"
            className={`transition-colors ${isActive("/tools")
              ? "text-[#4F46E5]"
              : "hover:text-gray-900 dark:hover:text-white"
              }`}
          >
            All Tools
          </Link>
          <Link
            href="/tools?view=categories"
            className={`transition-colors ${isToolsCategoriesActive
              ? "text-[#4F46E5]"
              : "hover:text-gray-900 dark:hover:text-white"
              }`}
          >
            Categories
          </Link>
          <Link
            href="/blog"
            className={`transition-colors ${isActive("/blog")
              ? "text-[#4F46E5]"
              : "hover:text-gray-900 dark:hover:text-white"
              }`}
          >
            Blog
          </Link>
          <Link
            href="/about-us"
            className={`transition-colors ${isActive("/about-us")
              ? "text-[#4F46E5]"
              : "hover:text-gray-900 dark:hover:text-white"
              }`}
          >
            About
          </Link>
        </nav>

        {/* Right side: search & theme toggle */}
        <div className="flex items-center gap-2">
          <form
            onSubmit={handleSearchSubmit}
            className={`hidden items-center gap-2 rounded-full border border-transparent bg-slate-50 px-3 py-1 text-sm text-gray-500 shadow-sm transition-all duration-200 ease-out focus-within:border-[#4F46E5]/40 focus-within:bg-white focus-within:shadow-md dark:bg-slate-900/40 dark:text-slate-300 dark:focus-within:bg-slate-900 lg:flex ${searchOpen
              ? "w-64 opacity-100"
              : "w-10 cursor-pointer justify-center pl-2 pr-2 opacity-80"
              }`}
          >
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              className="flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
            >
              <Search className="h-4 w-4" />
            </button>
            {searchOpen && (
              <input
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => {
                  if (!searchQuery) setSearchOpen(false);
                }}
                placeholder="Search tools…"
                className="h-7 flex-1 border-none bg-transparent text-sm text-gray-900 outline-none placeholder:text-gray-400 dark:text-slate-100"
              />
            )}
          </form>

          <button
            type="button"
            onClick={toggleTheme}
            aria-label="Toggle dark mode"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800"
          >
            {isDark ? (
              <SunMedium className="h-4 w-4" />
            ) : (
              <MoonStar className="h-4 w-4" />
            )}
          </button>

          <div className="hidden lg:block">
            <Link
              href="/tools"
              className="inline-flex items-center rounded-full bg-[#4F46E5] px-4 py-2 text-sm font-semibold text-white shadow-md shadow-indigo-500/30 transition hover:bg-[#4338CA] hover:shadow-lg"
            >
              Explore Tools
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen((v) => !v)}
            className="ml-1 inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800 lg:hidden"
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5" />
            ) : (
              <Menu className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="border-t border-slate-200 bg-white/95 px-4 pb-4 pt-2 shadow-md dark:border-slate-800 dark:bg-slate-950/95 lg:hidden">
          <nav className="flex flex-col gap-2 text-sm">
            <Link
              href="/tools"
              onClick={() => setMobileMenuOpen(false)}
              className={`rounded-xl px-3 py-2 font-medium transition ${isActive("/tools")
                ? "bg-slate-100 text-[#4F46E5] dark:bg-slate-900 dark:text-[#6366F1]"
                : "text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-900"
                }`}
            >
              All Tools
            </Link>
            <Link
              href="/tools?view=categories"
              onClick={() => setMobileMenuOpen(false)}
              className={`rounded-xl px-3 py-2 font-medium transition ${isToolsCategoriesActive
                ? "bg-slate-100 text-[#4F46E5] dark:bg-slate-900 dark:text-[#6366F1]"
                : "text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-900"
                }`}
            >
              Categories
            </Link>
            <Link
              href="/blog"
              onClick={() => setMobileMenuOpen(false)}
              className={`rounded-xl px-3 py-2 font-medium transition ${isActive("/blog")
                ? "bg-slate-100 text-[#4F46E5] dark:bg-slate-900 dark:text-[#6366F1]"
                : "text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-900"
                }`}
            >
              Blog
            </Link>
            <Link
              href="/about-us"
              onClick={() => setMobileMenuOpen(false)}
              className={`rounded-xl px-3 py-2 font-medium transition ${isActive("/about-us")
                ? "bg-slate-100 text-[#4F46E5] dark:bg-slate-900 dark:text-[#6366F1]"
                : "text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-900"
                }`}
            >
              About
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}